import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Users, Clock, Target, Award } from 'lucide-react';

const About = () => {
  const process = [
    {
      step: 1,
      title: 'Requirement Discussion',
      description: 'We understand your needs, budget, and brand objectives through detailed consultation.'
    },
    {
      step: 2,
      title: 'Product Curation',
      description: 'Our team handpicks the perfect products that align with your brand and message.'
    },
    {
      step: 3,
      title: 'Branding & Customisation',
      description: 'Custom branding, packaging, and personalization to make each gift unique.'
    },
    {
      step: 4,
      title: 'Production & Quality Check',
      description: 'Rigorous quality control ensures every product meets our premium standards.'
    },
    {
      step: 5,
      title: 'Delivery',
      description: 'Timely pan-India delivery with end-to-end order tracking and support.'
    },
  ];

  const industries = [
    { name: 'Startups', icon: <Target /> },
    { name: 'IT Companies', icon: <Users /> },
    { name: 'Finance & Banking', icon: <Award /> },
    { name: 'Consulting Firms', icon: <CheckCircle /> },
    { name: 'Retail Brands', icon: <Target /> },
    { name: 'Manufacturing', icon: <Clock /> },
  ];

  return (
    <div>
      <section className="py-24 md:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="about-label">
                About SwiftHampers
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6" data-testid="about-title">
                Your Trusted Partner in{' '}
                <span className="text-accent">Corporate Gifting</span>
              </h1>
              <div className="space-y-4 text-lg text-muted leading-relaxed">
                <p data-testid="about-description-1">
                  SwiftHampers is a modern corporate gifting and promotional merchandise company helping brands create memorable experiences through thoughtfully curated gifts and merchandise.
                </p>
                <p data-testid="about-description-2">
                  We partner with businesses to deliver custom branded products, employee welcome kits, event merchandise, and festive gifting solutions that strengthen relationships and amplify brand presence.
                </p>
                <p data-testid="about-description-3">
                  Our commitment to <strong>reliability</strong>, <strong>premium quality</strong>, <strong>fast turnaround</strong>, and a <strong>strong vendor network</strong> makes us the preferred choice for corporations across India.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1712903276839-8cb918b01e76?auto=format&fit=crop&q=80"
                alt="Corporate Gifting Excellence"
                className="rounded-2xl shadow-2xl w-full"
                data-testid="about-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="process-label">
              How We Work
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="process-title">
              Our Process
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto" data-testid="process-description">
              From consultation to delivery, we ensure a seamless experience at every step.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-accent text-white flex items-center justify-center text-2xl font-bold mb-4" data-testid={`process-step-number-${index}`}>
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold mb-2" data-testid={`process-step-title-${index}`}>{item.title}</h3>
                  <p className="text-sm text-muted" data-testid={`process-step-description-${index}`}>{item.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="industries-label">
              Who We Serve
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="industries-title">
              Client Industries
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto" data-testid="industries-description">
              We serve diverse industries with tailored gifting solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex flex-col items-center p-6 bg-white rounded-xl border border-slate-100 hover:border-accent/20 hover:shadow-lg transition-all duration-300"
                data-testid={`industry-${index}`}
              >
                <div className="text-accent mb-3" data-testid={`industry-icon-${index}`}>{industry.icon}</div>
                <p className="text-sm font-medium text-center" data-testid={`industry-name-${index}`}>{industry.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
